import React, { useState, useEffect } from 'react';
import { 
  List, ChevronRight, Clock, CheckCircle, Circle,
  Menu, X, ArrowUp, BarChart3
} from 'lucide-react';
import { TableOfContentsItem } from '../../types/enhancedAnalysis';

interface ContentNavigationProps {
  tableOfContents: TableOfContentsItem[];
  activeSection: string;
  readingProgress: number;
  onSectionClick: (sectionId: string) => void;
}

const ContentNavigation: React.FC<ContentNavigationProps> = ({
  tableOfContents,
  activeSection,
  readingProgress,
  onSectionClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 监听滚动显示回到顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionClick = (sectionId: string) => {
    onSectionClick(sectionId);
    if (isMobile) {
      setIsExpanded(false);
    }
  };

  const getSectionProgress = (sectionId: string) => {
    const sectionIndex = tableOfContents.findIndex(item => item.id === sectionId);
    const totalSections = tableOfContents.length;
    const sectionProgress = (sectionIndex + 1) / totalSections * 100;
    return Math.min(readingProgress, sectionProgress);
  };

  const isCompleted = (sectionId: string) => {
    return getSectionProgress(sectionId) >= 90;
  };

  if (isMobile) {
    return (
      <>
        {/* 移动端导航栏 */}
        <div className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              {isExpanded ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              <span className="font-medium">目录</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <BarChart3 className="w-4 h-4" />
                <span>{Math.round(readingProgress)}%</span>
              </div>
              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${readingProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* 移动端展开的目录 */}
          {isExpanded && (
            <div className="border-t border-gray-200 bg-white max-h-80 overflow-y-auto">
              <div className="py-2">
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSectionClick(item.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      activeSection === item.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {isCompleted(item.id) ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Circle className={`w-4 h-4 ${
                          activeSection === item.id ? 'text-blue-500' : 'text-gray-400'
                        }`} />
                      )}
                      <span className={`text-sm ${
                        activeSection === item.id ? 'text-blue-600 font-medium' : 'text-gray-700'
                      }`}>
                        {item.title}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 回到顶部按钮 */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 hover:scale-110"
          >
            <ArrowUp className="w-5 h-5 mx-auto" />
          </button>
        )}
      </>
    );
  }

  // 桌面端导航
  return (
    <div className="mb-8">
      {/* 进度条 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <List className="w-5 h-5 mr-2 text-blue-600" />
            阅读进度
          </h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>{Math.round(readingProgress)}% 完成</span>
          </div>
        </div>
        
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 transition-all duration-500 ease-out"
            style={{ width: `${readingProgress}%` }}
          />
        </div>

        <div className="text-xs text-gray-500">
          继续阅读以解锁更多深度洞察
        </div>
      </div>

      {/* 目录导航 */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <List className="w-5 h-5 mr-2 text-blue-600" />
            内容目录
          </h3>
        </div>
        
        <div className="py-2">
          {tableOfContents.map((item, index) => {
            const progress = getSectionProgress(item.id);
            const completed = isCompleted(item.id);
            
            return (
              <button
                key={item.id}
                onClick={() => handleSectionClick(item.id)}
                className={`w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-all duration-200 group ${
                  activeSection === item.id ? 'bg-blue-50 border-r-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  {/* 状态图标 */}
                  <div className="flex-shrink-0">
                    {completed ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <div className="relative">
                        <Circle className={`w-5 h-5 ${
                          activeSection === item.id ? 'text-blue-500' : 'text-gray-400'
                        }`} />
                        {progress > 0 && progress < 90 && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div 
                              className="w-3 h-3 bg-blue-500 rounded-full"
                              style={{ 
                                transform: `scale(${progress / 100})`,
                                transition: 'transform 0.3s ease'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 章节信息 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium truncate ${
                        activeSection === item.id ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {item.title}
                      </span>
                      {completed && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          已完成
                        </span>
                      )}
                    </div>
                    
                    {/* 进度条 */}
                    <div className="mt-2 w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          completed ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* 章节编号 */}
                  <div className="flex-shrink-0 text-xs text-gray-400 font-mono">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                </div>

                <ChevronRight className={`w-4 h-4 transition-all duration-200 ${
                  activeSection === item.id ? 'text-blue-500 translate-x-1' : 'text-gray-400 group-hover:translate-x-1'
                }`} />
              </button>
            );
          })}
        </div>

        {/* 底部统计 */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {tableOfContents.filter(item => isCompleted(item.id)).length} / {tableOfContents.length} 章节已完成
            </span>
            <span>
              预计还需 {Math.max(0, Math.ceil((100 - readingProgress) / 20))} 分钟
            </span>
          </div>
        </div>
      </div>

      {/* 回到顶部按钮 */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-14 h-14 bg-white border border-gray-300 text-gray-600 rounded-full shadow-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 hover:scale-110 group"
        >
          <ArrowUp className="w-5 h-5 mx-auto group-hover:-translate-y-0.5 transition-transform" />
        </button>
      )}
    </div>
  );
};

export default ContentNavigation;